import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SeoHead from '@/components/base/SeoHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { WebPageSchema, BreadcrumbListSchema } from '@/components/base/SchemaOrg';
import { categories, resources } from '@/mocks/resources';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const initialCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [selectedCategory, setSelectedCategory] = useState(initialCategory.slug);

  const filteredResources = useMemo(() => {
    return resources.filter((r) => r.categorySlug === selectedCategory);
  }, [selectedCategory]);

  const currentCategory = categories.find((c) => c.slug === selectedCategory) || categories[0];

  const handleCategoryChange = (newSlug: string) => {
    setSelectedCategory(newSlug);
  };

  return (
    <Layout>
      <SeoHead
        title={`${currentCategory.name} - 无码专区资源分类`}
        description={`无码专区${currentCategory.name}频道，${currentCategory.description}。共收录${currentCategory.count}个优质资源，全部免费下载。包含无码专区高清资源、免费下载、最新更新等精选内容。`}
        keywords={`无码专区${currentCategory.name},无码专区高清资源,无码专区免费下载,无码专区最新更新,${currentCategory.name}素材合集,无码专区资源导航`}
        canonicalUrl={`/category/${selectedCategory}`}
      />
      <WebPageSchema
        name={`${currentCategory.name} - 无码专区`}
        description={`无码专区${currentCategory.name}频道，${currentCategory.description}`}
        url={`https://aialhku.cn/category/${selectedCategory}`}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', item: 'https://aialhku.cn/' },
          { name: currentCategory.name, item: `https://aialhku.cn/category/${selectedCategory}` },
        ]}
      />

      <div className="pt-20 pb-14 md:pb-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: currentCategory.name }]} />

          <div className="text-center mb-10 mt-4">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              {currentCategory.description}
            </p>
          </div>

          <div className="flex items-center justify-center gap-1.5 flex-wrap mb-10">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  selectedCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
            {filteredResources.length > 0 ? (
              filteredResources.map((resource) => (
                <Link
                  key={resource.id}
                  to={`/article/${resource.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-52 overflow-hidden">
                    <img
                      src={resource.image}
                      alt={resource.title}
                      title={`${resource.title} - 无码专区${currentCategory.name}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-sm font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{resource.description}</p>
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      {resource.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-md whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span><i className="ri-eye-line mr-1"></i>{resource.views.toLocaleString()}</span>
                      <span><i className="ri-download-line mr-1"></i>{resource.downloads.toLocaleString()}</span>
                      <span>{resource.date}</span>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="col-span-full text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无资源，请浏览其他分类</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}